import { Scale } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { formatTrimmedDecimal } from '@/helpers/finance';
import { cn } from '@/lib/utils';

import { getOverheadTypeIcon } from '../../constants/overhead-type-icon-map';
import { OverheadsSummary, OverheadSummaryBucket, OverheadType } from '../../types/overhead';
import {
  computeOverheadsAnalysis,
  formatOverheadNetAmount,
  getNetPolarityClass,
} from './overheads-summary-analysis-card';
import { formatOverheadAmount } from './overheads-summary-card';

interface OverheadsSummaryCompactProps {
  summary: OverheadsSummary;
}

interface CompactChip {
  type: OverheadType;
  label: string;
  total: OverheadSummaryBucket;
  breakdown: Array<{ label: string; bucket: OverheadSummaryBucket }>;
}

const sumBuckets = (...buckets: OverheadSummaryBucket[]): OverheadSummaryBucket =>
  buckets.reduce(
    (acc, bucket) => ({ amount: acc.amount + bucket.amount, count: acc.count + bucket.count }),
    { amount: 0, count: 0 }
  );

/**
 * Collapsed rendition of the overheads summary: one chip per overhead kind
 * with the period total, and the full breakdown in a tooltip.
 */
export const OverheadsSummaryCompact = ({ summary }: OverheadsSummaryCompactProps) => {
  const { t } = useTranslation(['authenticated.overheads', 'common']);

  const chips: CompactChip[] = [
    {
      type: OverheadType.Income,
      label: t('summary.incomeTitle'),
      total: sumBuckets(summary.incomePending, summary.incomeInvoiced),
      breakdown: [
        { label: t('summary.rows.pending'), bucket: summary.incomePending },
        { label: t('summary.rows.invoiced'), bucket: summary.incomeInvoiced },
      ],
    },
    {
      type: OverheadType.Cost,
      label: t('summary.costTitle'),
      total: sumBuckets(summary.costPlanned, summary.costPaid),
      breakdown: [
        { label: t('summary.rows.planned'), bucket: summary.costPlanned },
        { label: t('summary.rows.paid'), bucket: summary.costPaid },
      ],
    },
  ];

  const renderTooltip = (chip: CompactChip) => (
    <div className="flex min-w-[200px] flex-col gap-1">
      <p className="text-xs font-semibold">{chip.label}</p>
      {[...chip.breakdown, { label: t('summary.rows.total'), bucket: chip.total }].map(row => (
        <div key={row.label} className="flex items-center justify-between gap-3 text-xs">
          <span className="text-muted-foreground">{row.label}</span>
          <span className="font-medium">
            {formatOverheadAmount(row.bucket.amount, summary.currencyCode)}
            <span className="text-muted-foreground">
              {` / ${t('summary.entriesCount', { count: row.bucket.count })}`}
            </span>
          </span>
        </div>
      ))}
    </div>
  );

  const analysis = computeOverheadsAnalysis(summary);

  const renderNetTooltip = () => (
    <div className="flex min-w-[200px] flex-col gap-1">
      <p className="text-xs font-semibold">{t('summary.analysis.title')}</p>
      {[
        { label: t('summary.analysis.income'), value: analysis.positiveTotal },
        { label: t('summary.analysis.cost'), value: analysis.negativeTotal },
        { label: t('summary.analysis.netCompact'), value: analysis.net },
      ].map(row => (
        <div key={row.label} className="flex items-center justify-between gap-3 text-xs">
          <span className="text-muted-foreground">{row.label}</span>
          <span className="font-medium">
            {formatOverheadAmount(row.value, summary.currencyCode)}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-muted-foreground">{t('summary.analysis.marginCompact')}</span>
        <span className="font-medium">
          {analysis.marginPct === null
            ? t('common:table.noValue')
            : `${formatTrimmedDecimal(analysis.marginPct, 1)}%`}
        </span>
      </div>
    </div>
  );

  const chipClassName = cn(
    'flex shrink-0 items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5',
    'text-xs font-medium text-foreground transition-colors hover:bg-muted/70',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  );

  return (
    <div className="flex h-full items-center bg-background">
      <span className="mr-3 shrink-0 text-lg font-semibold">{t('summary.compactTitle')}</span>
      <div className="flex flex-1 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {chips.map(chip => {
          const Icon = getOverheadTypeIcon(chip.type);
          return (
            <SimpleTooltip key={chip.type} content={renderTooltip(chip)}>
              <button
                type="button"
                className={chipClassName}
                aria-label={`${chip.label}: ${formatOverheadAmount(chip.total.amount, summary.currencyCode)}`}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
                  <Icon className="h-3 w-3" />
                </span>
                <span className="max-w-[120px] truncate text-muted-foreground">{chip.label}</span>
                <span className="font-semibold tabular-nums">
                  {formatOverheadAmount(chip.total.amount, summary.currencyCode)}
                </span>
              </button>
            </SimpleTooltip>
          );
        })}
        <SimpleTooltip content={renderNetTooltip()}>
          <button
            type="button"
            className={chipClassName}
            aria-label={`${t('summary.analysis.netCompact')}: ${formatOverheadNetAmount(analysis.net, summary.currencyCode)}`}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
              <Scale className="h-3 w-3" />
            </span>
            <span className="max-w-[120px] truncate text-muted-foreground">
              {t('summary.analysis.netCompact')}
            </span>
            <span className={cn('font-semibold tabular-nums', getNetPolarityClass(analysis.net))}>
              {formatOverheadNetAmount(analysis.net, summary.currencyCode)}
            </span>
          </button>
        </SimpleTooltip>
      </div>
    </div>
  );
};
